/**
 * Human-readable labels for Operate digital workers and skills.
 */
import { manifestSkill } from './skillsManifest';

export interface OperateWorkerDisplay {
  id: string;
  displayName: string;
  emoji: string;
  description: string;
}

const WORKER_META: Record<string, Omit<OperateWorkerDisplay, 'id'>> = {
  steward: {
    displayName: 'Repository Steward',
    emoji: '🛡️',
    description: 'Ingests codebases and keeps repository baselines ready for incident correlation.',
  },
  architect: {
    displayName: 'Architecture Analyst',
    emoji: '🏗️',
    description: 'Maps services, boundaries, and dependency blast radius during investigations.',
  },
  developer: {
    displayName: 'Code Path Analyst',
    emoji: '💻',
    description: 'Traces failing code paths and links alerts to repository artifacts.',
  },
  sensei: {
    displayName: 'Pattern Advisor',
    emoji: '🎓',
    description: 'Surfaces documented patterns, runbooks, and prior repair knowledge.',
  },
  forensic: {
    displayName: 'Change Investigator',
    emoji: '🔍',
    description: 'Follows recent commits, config drift, and dependency changes.',
  },
  profiler: {
    displayName: 'Performance Profiler',
    emoji: '📊',
    description: 'Identifies latency hotspots, complexity spikes, and resource pressure.',
  },
  crystal: {
    displayName: 'Business Impact Analyst',
    emoji: '🔮',
    description: 'Estimates operational and business impact of active incidents.',
  },
  security: {
    displayName: 'Security Analyst',
    emoji: '🔒',
    description: 'Evaluates exposure, secrets risk, and endpoint security posture.',
  },
  'repository-scanner': {
    displayName: 'Repository Scanner',
    emoji: '📡',
    description: 'Indexes repository files for static correlation with incident signals.',
  },
  scanner: {
    displayName: 'Repository Scanner',
    emoji: '📡',
    description: 'Indexes repository files for static correlation with incident signals.',
  },
};

const SKILL_OPERATE_LABELS: Record<string, Omit<OperateWorkerDisplay, 'id'>> = {
  'incident-intel': {
    displayName: 'Incident Investigator',
    emoji: '🚨',
    description: 'Triages inbound alerts and assembles the investigation context.',
  },
  'ops-learning': {
    displayName: 'Operations Learner',
    emoji: '📚',
    description: 'Stores repair episodes and reusable operational knowledge.',
  },
  capacity: {
    displayName: 'Capacity Monitor',
    emoji: '📈',
    description: 'Tracks throughput, saturation, and infrastructure headroom.',
  },
  'business-forecast': {
    displayName: 'Impact Forecaster',
    emoji: '🎯',
    description: 'Forecasts downstream impact from active incident signals.',
  },
  'digital-twin': {
    displayName: 'Runtime Twin',
    emoji: '🌐',
    description: 'Mirrors production topology for correlation and simulation.',
  },
  'legacy-intel': {
    displayName: 'Legacy Intelligence',
    emoji: '🗂️',
    description: 'Extracts legacy semantics and hidden dependencies from artifacts.',
  },
  'sdlc-continuity': {
    displayName: 'Delivery Continuity',
    emoji: '🔄',
    description: 'Ensures ingest-to-operate handoff stays continuous and auditable.',
  },
};

function normalizeKey(id: string): string {
  return id.toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
}

function humanize(raw: string): string {
  return raw
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Resolve a platform worker or skill id to a user-facing Operate label + description. */
export function resolveOperateWorkerDisplay(
  id: string,
  fallbackName?: string,
  fallbackDescription?: string,
): OperateWorkerDisplay {
  const key = normalizeKey(id);
  const meta = WORKER_META[key] ?? WORKER_META[id.toLowerCase()];
  if (meta) return { id, ...meta };

  const skill = SKILL_OPERATE_LABELS[key] ?? SKILL_OPERATE_LABELS[id];
  if (skill) return { id, ...skill };

  const manifest = manifestSkill(id) ?? manifestSkill(key);
  if (manifest) {
    return {
      id,
      displayName: manifest.name,
      emoji: '⚙️',
      description: manifest.summary,
    };
  }

  const nameKey = normalizeKey(fallbackName || '');
  const byName = WORKER_META[nameKey];
  if (byName) return { id, ...byName };

  return {
    id,
    displayName: humanize(fallbackName || id),
    emoji: '⚙️',
    description: fallbackDescription || 'Supports autonomous incident investigation and repair.',
  };
}

export type IncidentSignalKind = 'email' | 'log' | 'edr' | 'runtime' | 'other';

/** Classify incident ingest channel from backend source + payload hints. */
export function classifyIncidentSignal(
  source?: string | null,
  payload?: Record<string, unknown> | null,
): IncidentSignalKind {
  const src = (source || '').toLowerCase();
  if (src.includes('email') || src.includes('inbox') || src.includes('mailbox')) return 'email';
  if (payload?.subject || payload?.Subject || payload?.email_subject) return 'email';
  if (src.includes('edr') || src.includes('endpoint') || src.includes('crowd')) return 'edr';
  if (src.includes('log') || src === 'runtime_log') return 'log';
  if (src.includes('runtime') || src.includes('sftp') || src.includes('kube')) return 'runtime';
  return 'other';
}

export const SIGNAL_KIND_META: Record<
  IncidentSignalKind,
  { label: string; emoji: string; description: string }
> = {
  email: {
    label: 'Email / inbox',
    emoji: '📧',
    description: 'Operational alerts ingested from connected mailboxes.',
  },
  log: {
    label: 'Log streams',
    emoji: '📜',
    description: 'Runtime and application log signals correlated to services.',
  },
  edr: {
    label: 'EDR / Endpoint',
    emoji: '🛡️',
    description: 'Endpoint detection and runtime health telemetry.',
  },
  runtime: {
    label: 'Runtime / SFTP',
    emoji: '⚡',
    description: 'Live runtime probes, SFTP sync, and platform alerts.',
  },
  other: {
    label: 'Other signals',
    emoji: '📡',
    description: 'Additional AMS ingest channels reported by the backend.',
  },
};
